import React from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CircularProgress, Box, Typography } from "@mui/material";
import { selectIsAuthenticated } from "../../store/slices/authSlice";
import { useUserRoles } from "../../hooks/useUserRoles";
import { UserRoleType } from "../../store/services/userService";

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRoleType[];
  requireAll?: boolean; // If true, user must have ALL roles. If false (default), user needs ANY of the roles
  fallback?: React.ReactNode; // Custom component to show when unauthorized
  redirectTo?: string; // Custom redirect path (default: /unauthorized)
}

export function RoleProtectedRoute({
  children,
  allowedRoles,
  requireAll = false,
  fallback,
  redirectTo = "/unauthorized",
}: RoleProtectedRouteProps) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { currentUser, isLoading, hasAnyRole, hasAllRoles } = useUserRoles();

  // First check authentication
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show loading state while fetching user data
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Kontrollerar behörigheter...
        </Typography>
      </Box>
    );
  }

  // Check if user data was loaded successfully
  if (!currentUser) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography variant="h6" color="error">
          Kunde inte hämta användarinformation
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Vänligen försök igen eller kontakta support
        </Typography>
      </Box>
    );
  }

  // Check role permissions
  const hasPermission = requireAll
    ? hasAllRoles(allowedRoles)
    : hasAnyRole(allowedRoles);

  if (!hasPermission) {
    // If custom fallback is provided, show it
    if (fallback) {
      return <>{fallback}</>;
    }
    // Otherwise redirect to unauthorized page
    return <Navigate to={redirectTo} replace />;
  }

  // User has required permissions, render children
  return <>{children}</>;
}